import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { Button, IconButton, StarRating, useToast } from '../../ui';
import { CheckIcon, CloseIcon, EditIcon, LockIcon } from '../../ui/icons';
import { formatDate } from '../FieldValue';
import { isShared, personalOf, starNumber, today } from './personal';

const NOTE_MAX = 1000;
const NOTE_PAUSE_MS = 1000;
const EMPTY = { rating: null, done: false, done_on: null, note: null };

/** Tomorrow, the latest date the server accepts (the browser's day may be ahead). */
const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** A full date the server accepts: from year 1000 to tomorrow (a partly typed one is not). */
const validDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= '1000-01-01' && value <= tomorrow()
  && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

/**
 * "My opinion" (spec §2.4): my rating, "seen" (the type's verb) with its
 * date, my private note, and the members' average on the right. Each
 * change is saved at once (optimistic; a failure restores the previous value
 * and says so); the note after a pause of a second, and when it is left.
 */
function MyOpinion({ inventory, item, itemType, onSaved }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const toast = useToast();
  const settings = personalOf(itemType);
  const [mine, setMine] = useState({ ...EMPTY, ...(item.mine || {}) });
  const [ratings, setRatings] = useState(item.ratings || { average: null, count: 0 });
  const [note, setNote] = useState((item.mine && item.mine.note) || '');
  const [editingDate, setEditingDate] = useState(false);
  const savedNote = useRef((item.mine && item.mine.note) || '');
  const noteId = useId();
  const dateId = useId();
  // What the server last said, the changes still on their way (in order), and
  // the queue that sends them one after the other.
  const confirmed = useRef({ ...EMPTY, ...(item.mine || {}) });
  const pending = useRef([]);
  const queue = useRef(Promise.resolve());
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  // Shown: the server's last answer with the pending changes over it.
  const show = () => {
    const next = pending.current.reduce((acc, changes) => ({ ...acc, ...changes }), confirmed.current);
    setMine(next.done ? next : { ...next, done_on: null });
  };

  // Optimistic and serialised: shown at once, sent after the writes before it;
  // each answer becomes the truth, a refused change disappears (and is said).
  const save = (changes) => {
    pending.current = [...pending.current, changes];
    show();
    const send = async () => {
      try {
        const response = await api.put(`/inventories/${inventory.id}/items/${item.id}/mine`, changes);
        confirmed.current = { ...EMPTY, ...(response.data.mine || {}) };
        setRatings(response.data.ratings);
        if (onSavedRef.current) onSavedRef.current(response.data);
        return true;
      } catch (error) {
        toast.error(t('item.opinion.saveFailed'));
        return false;
      } finally {
        pending.current = pending.current.filter((entry) => entry !== changes);
        show();
      }
    };
    const done = queue.current.then(send);
    queue.current = done;
    return done;
  };

  const saveNote = async (text) => {
    const value = text.trim();
    if (value === savedNote.current.trim()) return;
    const previous = savedNote.current;
    savedNote.current = value;
    if (!(await save({ note: value === '' ? null : value }))) savedNote.current = previous;
  };
  // A pause in typing saves the note.
  useEffect(() => {
    if (note.trim() === savedNote.current.trim()) return undefined;
    const timer = setTimeout(() => saveNote(note), NOTE_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [note]); // eslint-disable-line react-hooks/exhaustive-deps

  const verb = settings.done;
  const valueText = (v) => t('item.opinion.stars', { count: v, value: starNumber(v, lang) });
  const showAverage = settings.rating && isShared(inventory) && ratings.count > 0;

  const toggleDone = () => {
    setEditingDate(false);
    save(mine.done ? { done: false } : { done: true, done_on: today() });
  };

  return (
    <section aria-label={t('item.opinion.title')} className="my-opinion">
      {settings.rating && (
        <div className="my-opinion__group">
          <span className="my-opinion__label" aria-hidden="true">{t('item.opinion.myRating')}</span>
          <div className="my-opinion__rating">
            <StarRating value={mine.rating} onChange={(rating) => save({ rating })}
                        label={t('item.opinion.ratingLabel')} valueText={valueText} />
            <span className="my-opinion__value" aria-live="polite">
              {mine.rating != null ? valueText(mine.rating) : t('item.opinion.notRated')}
            </span>
            {mine.rating != null && (
              <Button variant="ghost" size="sm" onClick={() => save({ rating: null })}>{t('item.opinion.clearRating')}</Button>
            )}
          </div>
        </div>
      )}

      {verb && (
        <div className="my-opinion__group my-opinion__done">
          <label className={`my-opinion__toggle${mine.done ? ' is-on' : ''}`}>
            <input type="checkbox" checked={!!mine.done} onChange={toggleDone} />
            <span>
              {mine.done && mine.done_on
                ? t(`item.opinion.doneOn.${verb}`, { date: formatDate(mine.done_on, lang) })
                : t(`item.opinion.verb.${verb}`)}
            </span>
          </label>
          {mine.done && !editingDate && (mine.done_on
            ? <IconButton icon={EditIcon} variant="ghost" size="sm" label={t('item.opinion.changeDate')} onClick={() => setEditingDate(true)} />
            : <Button variant="ghost" size="sm" onClick={() => setEditingDate(true)}>{t('item.opinion.addDate')}</Button>)}
          {mine.done && editingDate && (
            <span className="my-opinion__date">
              <label htmlFor={dateId} className="visually-hidden">{t(`item.opinion.dateLabel.${verb}`)}</label>
              <input id={dateId} type="date" className="my-opinion__date-input" defaultValue={mine.done_on || ''} max={tomorrow()}
                     onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditingDate(false); } }}
                     onChange={(e) => { const day = e.target.value; if (validDay(day) && day !== mine.done_on) save({ done_on: day }); }} />
              {mine.done_on && (
                <Button variant="ghost" size="sm" icon={CloseIcon} onClick={() => { setEditingDate(false); save({ done_on: null }); }}>
                  {t('item.opinion.removeDate')}
                </Button>
              )}
              <Button variant="ghost" size="sm" icon={CheckIcon} onClick={() => setEditingDate(false)}>{t('item.opinion.dateDone')}</Button>
            </span>
          )}
        </div>
      )}

      {showAverage && (
        <div className="my-opinion__average">
          <span className="my-opinion__label">{t('item.opinion.average')}</span>
          <span className="my-opinion__average-value">
            {starNumber(ratings.average, lang)}
            {' '}
            <span className="my-opinion__average-count">{t('item.opinion.averageOf', { count: ratings.count })}</span>
          </span>
        </div>
      )}

      <div className="my-opinion__note">
        <label htmlFor={noteId} className="my-opinion__label">{t('item.opinion.note')}</label>
        <textarea
          id={noteId}
          className="ui-textarea my-opinion__note-input"
          rows={Math.min(4, Math.max(1, note.split('\n').length + Math.floor(note.length / 80)))}
          maxLength={NOTE_MAX}
          value={note}
          placeholder={t('item.opinion.notePlaceholder')}
          aria-describedby={`${noteId}-hint`}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => saveNote(note)}
        />
        <p id={`${noteId}-hint`} className="ui-field__hint my-opinion__private">
          <LockIcon size={14} /> {t('item.opinion.private')}
        </p>
      </div>
    </section>
  );
}

export default MyOpinion;
